import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { statusColor } from "@/lib/utils";
import { toast } from "sonner";
import { Plus, Trash2, Camera, Pencil } from "lucide-react";

const EMPTY = { plate: "", model: "", type: "hiace", capacity: 12, year: 2024, image: "", status: "available", price_per_km: 5, description: "", features: "" };
const STATUSES = ["available", "in-service", "maintenance"];

export function AdminFleet() {
  const { t, site } = useApp();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(null);
  const fileRef = useRef();
  const vt = site?.vehicle_types || {};
  const load = () => api.get("/vehicles").then((r) => setRows(r.data.items));
  useEffect(() => { load(); }, []);
  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (v) => { setForm({ ...EMPTY, ...v }); setEditId(v.id); setOpen(true); };
  const save = async () => {
    try {
      if (editId) await api.put(`/vehicles/${editId}`, form); else await api.post("/vehicles", form);
      toast.success(t("saved")); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("failed")); }
  };
  const del = async (id) => { if (window.confirm(t("delete") + "?")) { await api.delete(`/vehicles/${id}`); load(); } };
  const pickPhoto = (id) => { setUploading(id); fileRef.current.click(); };
  const uploadPhoto = async (file) => {
    if (!file || !uploading) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/vehicles/${uploading}/photo`, fd); toast.success(t("photo_updated")); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("failed")); } finally { setUploading(null); }
  };
  return (
    <div className="space-y-4">
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { uploadPhoto(e.target.files[0]); e.target.value = ""; }} data-testid="veh-photo-input" />
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">{t("armada")}</h1>
        <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={openNew} data-testid="btn-add-vehicle"><Plus className="w-4 h-4 mr-1" />{t("add_vehicle")}</Button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-2xl"><DialogHeader><DialogTitle>{editId ? t("edit_vehicle") : t("add_vehicle")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t("plate")}</Label><Input value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value })} data-testid="veh-plate" /></div>
            <div><Label>{t("model")}</Label><Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} data-testid="veh-model" /></div>
            <div><Label>{t("vehicle_type")}</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v, capacity: vt[v]?.capacity || form.capacity })}><SelectTrigger data-testid="veh-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{Object.entries(vt).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>{t("status")}</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger data-testid="veh-status"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{STATUSES.map((s) => <SelectItem key={s} value={s}>{t(`vs_${s}`)}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>{t("capacity")}</Label><Input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: +e.target.value })} data-testid="veh-capacity" /></div>
            <div><Label>{t("year")}</Label><Input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: +e.target.value })} data-testid="veh-year" /></div>
            <div className="col-span-2"><Label>{t("vehicle_features")}</Label><Input value={form.features || ""} onChange={(e) => setForm({ ...form, features: e.target.value })} placeholder={t("vehicle_features_ph")} data-testid="veh-features" /></div>
            <div className="col-span-2"><Label>{t("description")}</Label><Textarea rows={3} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={t("vehicle_desc_ph")} data-testid="veh-description" /></div>
            {!editId && <div className="col-span-2"><Label>{t("image_url_optional")}</Label><Input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} data-testid="veh-image-url" /></div>}
          </div>
          <Button className="bg-emerald-600 hover:bg-emerald-700 mt-3" onClick={save} data-testid="veh-save">{t("save")}</Button>
        </DialogContent>
      </Dialog>
      <div className="grid md:grid-cols-3 gap-4">
        {rows.map((v) => (
          <div key={v.id} className="abdan-card overflow-hidden" data-testid={`vehicle-${v.plate}`}>
            <div className="h-40 bg-slate-100 relative">
              {v.image && <img src={v.image} alt={v.model} className="w-full h-full object-cover" />}
              <Button size="sm" variant="secondary" className="absolute bottom-2 right-2 opacity-90" onClick={() => pickPhoto(v.id)} disabled={uploading === v.id} data-testid={`veh-photo-${v.plate}`}>
                <Camera className="w-4 h-4 mr-1" />{uploading === v.id ? "..." : t("photo")}</Button>
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between mb-1"><h3 className="font-bold">{v.model}</h3><Badge className={statusColor(v.status)}>{t(`vs_${v.status}`)}</Badge></div>
              <div className="text-sm font-mono text-slate-500">{v.plate} · {v.year}</div>
              {v.description && <p className="text-xs text-slate-600 mt-2 line-clamp-2" data-testid={`veh-desc-${v.plate}`}>{v.description}</p>}
              <div className="flex items-center justify-between mt-3 pt-2 border-t">
                <div className="text-xs text-slate-500">{v.capacity} pax · {vt[v.type]?.label || v.type}</div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(v)} data-testid={`veh-edit-${v.plate}`}><Pencil className="w-4 h-4 text-slate-600" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => del(v.id)} data-testid={`veh-del-${v.plate}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
